import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { DocumentReference, collection, deleteDoc, getDocs } from 'firebase/firestore';
import React, { useState } from 'react';

import { PollResponse } from '../../types';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

interface DeletedResponse extends PollResponse {
  voter: string;
  tabDocPath: string;
} 

const RemoveTabMismatch: React.FC = () => {
  const { user } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [currentActivity, setCurrentActivity] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [removedResponses, setRemovedResponses] = useState<DeletedResponse[]>([]);

  const deleteDocAndChildren = async (docRef: DocumentReference) => {
    const childDocs = await getDocs(collection(docRef, "pollResponses"));
    for (const childDoc of childDocs.docs) {
      await deleteDoc(childDoc.ref);
    }
    await deleteDoc(docRef);
  };

  const checkVoters = async (city: string, precinctDocRef: DocumentReference) => {
    const voters = collection(precinctDocRef, "voters");
    const votersSnap = await getDocs(voters);
    for (const voterDoc of votersSnap.docs) {
      console.log(voterDoc.id);
      setCurrentActivity(`Checking ${voterDoc.id}`);
      const response = voterDoc.data() as PollResponse;
      if (!response.pollTitle.trim().toUpperCase().startsWith(city)) {
        setCurrentActivity(`Deleting ${response.id} of ${voterDoc.id} in ${city}`);
        // delete the response
        await deleteDocAndChildren(voterDoc.ref);
        setRemovedResponses(prevResponses => [...prevResponses, {
          ...response,
          voter: voterDoc.id,
          tabDocPath: voterDoc.ref.path
        }]);
      }
    }
  };

  const checkPrecincts = async (city: string, barangayDocRef: DocumentReference) => {
    const precincts = collection(barangayDocRef, "precincts");
    const precinctsSnap = await getDocs(precincts);
    for (const precinctDoc of precinctsSnap.docs) {
      console.log(precinctDoc.id);
      setCurrentActivity(`Checking ${precinctDoc.id}`);
      await checkVoters(city, precinctDoc.ref);
    }
  };

  const checkBarangays = async (city: string, cityDocRef: DocumentReference) => {
    const barangays = collection(cityDocRef, "barangays");
    const barangaysSnap = await getDocs(barangays);
    for (const barangayDoc of barangaysSnap.docs) {
      console.log(barangayDoc.id);
      setCurrentActivity(`Checking ${barangayDoc.id}`);
      await checkPrecincts(city, barangayDoc.ref);
    }
  };

  const checkCities = async () => {
    const citiesColRef = collection(db, "tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts/DISTRICT 1/cities");
    const citiesSnap = await getDocs(citiesColRef);
    for (const cityDoc of citiesSnap.docs) {
      console.log(cityDoc.id);
      setCurrentActivity(`Checking ${cityDoc.id}`);
      await checkBarangays(cityDoc.id, cityDoc.ref);
    }
  };

  const handleRemoveTabMismatch = async () => {
    if (!user) {
      setSnackbarMessage('You must be logged in to perform this action');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsProcessing(true);
    setCurrentActivity('Initiating tabulation mismatch removal process...');
    setRemovedResponses([]); // Reset removed responses before starting

    try {
      await checkCities();
      
      setSnackbarMessage('Tabulation mismatch removal completed successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setCurrentActivity('Tabulation mismatch removal completed successfully');
    } catch (error) {
      console.error('Error removing tabulation mismatch:', error);
      setSnackbarMessage('Failed to remove tabulation mismatch');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setCurrentActivity('Tabulation mismatch removal failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Remove Tabulation Mismatch
      </Typography>
      <Typography variant="body1" paragraph>
        This utility helps remove inconsistencies in tabulation data across different levels.
      </Typography>
      <Button 
        variant="contained" 
        color="primary" 
        onClick={handleRemoveTabMismatch}
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : 'Remove Tabulation Mismatch'}
      </Button>
      
      {currentActivity && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1">
            Current Activity:
          </Typography>
          <Typography variant="body2" color={isProcessing ? 'text.secondary' : 'text.primary'}>
            {currentActivity}
          </Typography>
        </Box>
      )}

      {removedResponses.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Removed Responses ({removedResponses.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Voter</TableCell>
                  <TableCell>Tabulation Path</TableCell>
                  <TableCell>Poll Title</TableCell>
                  <TableCell>Submitted By</TableCell>
                  <TableCell>Response ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {removedResponses.map((response, index) => (
                  <TableRow key={index}>
                    <TableCell>{response.voter}</TableCell>
                    <TableCell>{response.tabDocPath}</TableCell>
                    <TableCell>{response.pollTitle}</TableCell>
                    <TableCell>{response.submittedBy}</TableCell>
                    <TableCell>{response.id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RemoveTabMismatch;
